import {
  collection,
  collectionGroup,
  type DocumentData,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore/lite";

import moment from "moment";
import { db } from "./firebase";

export const getUserIdeas = async (
  project: any,
  userId: string,
  ideaMap: object,
) => {
  if (!project.organizationId)
    throw new Error("OrganizationId not found in getUserIdeas");
  if (!project.projectId)
    throw new Error("ProjectId not found in getUserIdeas");
  if (!userId) throw new Error("UserId not found in getUserIdeas");

  const userIdeas = [] as (DocumentData & { ideaId: string })[];
  const ideasRef = collection(
    db,
    `organizations/${project.organizationId}/projects/${project.projectId}/ideas`,
  );
  const q = query(ideasRef, where("userId", "==", userId));
  const q1 = query(
    q,
    where("ideaMapId", "==", ideaMap.ideaMapId),
    orderBy("createdAt", "desc"),
  );
  const ideasSnapshot = await getDocs(q1);
  if (ideasSnapshot.empty) return [];

  ideasSnapshot.forEach((doc) => {
    userIdeas.push({
      ...doc.data(),
      ideaId: doc.id,
      body: `${doc.data().body.substring(0, 170)}...`,
      color:
        ideaMap?.customTopicsObject?.[doc.data().topic]?.color || "#fed957",
    });
  });
  return userIdeas;
};
export const hideIdea = async (project: any, ideaId: string) => {
  if (!project.organizationId)
    throw new Error("OrganizationId not found in hideIdea");
  if (!project.projectId) throw new Error("ProjectId not found in hideIdea");
  if (!ideaId) throw new Error("IdeaId not found in hideIdea");

  const ideaDocRef = doc(
    db,
    `organizations/${project.organizationId}/projects/${project.projectId}/ideas/${ideaId}`,
  );
  await updateDoc(ideaDocRef, {
    isHidden: true,
  });
};
export const unHideIdea = async (project: any, ideaId: string) => {
  if (!project.organizationId)
    throw new Error("OrganizationId not found in unHideIdea");
  if (!project.projectId) throw new Error("ProjectId not found in unHideIdea");
  if (!ideaId) throw new Error("IdeaId not found in unHideIdea");

  const ideaDocRef = doc(
    db,
    `organizations/${project.organizationId}/projects/${project.projectId}/ideas/${ideaId}`,
  );
  await updateDoc(ideaDocRef, {
    isHidden: false,
  });
};

export const getUserComments = async (userId: string) => {
  const commentsRef = collection(db, "comments");
  const commentsQuery = query(commentsRef, where("userId", "==", userId));
  const commentsQuerySnapshot = await getDocs(commentsQuery);
  if (commentsQuerySnapshot.empty) return [];
  const comments = [] as (DocumentData & { commentId: string })[];
  commentsQuerySnapshot.forEach((doc) =>
    comments.push({
      ...doc.data(),
      commentId: doc.id,
    }),
  );
  return comments;
};
export const getUserLikes = async (userId: string) => {
  const likesRef = collection(db, "likes");
  const likesQuery = query(likesRef, where("userId", "==", userId));
  const likesQuerySnapshot = await getDocs(likesQuery);
  if (likesQuerySnapshot.empty) return [];
  const likes = [] as (DocumentData & { likeId: string })[];
  likesQuerySnapshot.forEach((doc) =>
    likes.push({ ...doc.data(), likeId: doc.id }),
  );
  return likes;
};

export const getUserSurveyEntries = async (userId: string) => {
  const surveyEntries = [] as DocumentData[];

  const userEntriesQuery = query(
    collectionGroup(db, "entries"),
    where("userId", "==", userId),
  );
  const userEntriesSnapshot = await getDocs(userEntriesQuery);

  const promise = new Promise((resolve, reject) => {
    if (userEntriesSnapshot.size === 0) {
      resolve([]);
      return;
    }
    userEntriesSnapshot.forEach(async (entryDoc) => {
      surveyEntries.push({
        ...entryDoc.data(),
      });

      if (surveyEntries.length === userEntriesSnapshot.size) {
        return resolve(surveyEntries);
      }
    });
  });

  return promise;
};

export const getUserOrganizations = async (
  userId: string,
  isSuperAdmin = false,
) => {
  try {
    const membersQuery = query(
      collectionGroup(db, "members"),
      where("userId", "==", userId),
    );
    const superAdminQuery = query(
      collection(db, "organizations"),
      orderBy("createdAt", "desc"),
    );

    const organizationMembersSnap = await getDocs(
      isSuperAdmin ? superAdminQuery : membersQuery,
    );

    if (organizationMembersSnap.empty) return [];

    const promises = organizationMembersSnap.docs.map(async (memberDoc) => {
      const organizationId = memberDoc.data()?.organizationId;
      if (memberDoc.data()?.pending) {
        updateDoc(memberDoc.ref, {
          pending: false,
        });
      }
      if (typeof organizationId === "string") {
        const organizationRef = doc(db, "organizations", organizationId);
        const organizationDoc = await getDoc(organizationRef);

        if (organizationDoc.data()?.organizationId) {
          return {
            ...organizationDoc.data(),
            value: organizationDoc.id,
            label: organizationDoc.data()?.title,
            organizationId: organizationDoc.id,
          };
        }
      }
    });

    const organizations = await Promise.all(promises);
    return organizations.filter((org) => org !== undefined);
  } catch (error) {
    console.error(error);
  }
};

// Submit a comment to an idea
export const submitComment = async (
  project,
  ideaMapId,
  ideaId,
  commentData,
  user,
  device,
) => {
  if (!project.organizationId)
    throw new Error("OrganizationId not found in submitComment");
  if (!project.projectId)
    throw new Error("ProjectId not found in submitComment");
  if (!ideaId) throw new Error("IdeaId not found in submitComment");
  if (!commentData) throw new Error("CommentData not found in submitComment");
  if (!user) throw new Error("User not found in submitComment");
  try {
    const ideaDocRef = doc(
      db,
      `organizations/${project.organizationId}/projects/${project.projectId}/ideas/${ideaId}`,
    );
    const ideaDocSnapshot = await getDoc(ideaDocRef);
    if (!ideaDocSnapshot.exists()) {
      throw new Error("idea not found in submitComment");
    }
    if (!user.userId) {
      throw new Error("User not authenticated in submitComment");
    }

    // fetch latest userHandle from db
    const userDocRef = doc(db, `users/${user.userId}`);
    const userDocSnapshot = await getDoc(userDocRef);
    if (!userDocSnapshot.exists()) {
      throw new Error("user not found in submitComment");
    }
    const userHandle = userDocSnapshot.data()?.handle || null;
    const photoURL = userDocSnapshot.data()?.photoURL || null;

    const newComment = {
      title: null,
      commentId: "",
      projectId: project.projectId,
      ideaMapId,
      body: commentData.body,
      createdAt: new Date().toISOString(),
      ideaId,
      userHandle,
      photoURL,
      userId: user.userId,
      topic: ideaDocSnapshot.data().topic,
      device,
    };
    if (user.utm_id) {
      newComment.utm_id = user.utm_id;
    }

    // The List compoonent needs a title, not a body – rewrite db and convert body to title
    const newCommentConverted = newComment;
    newCommentConverted.title = newComment.body;

    // Create a new document reference with a generated ID
    const newCommentRef = doc(collection(db, "comments"));

    newCommentConverted.commentId = newCommentRef.id;
    await setDoc(newCommentRef, newComment);
    await updateDoc(ideaDocRef, {
      commentCount: increment(1),
    });

    return newCommentConverted;
  } catch (error) {
    throw new Error(error);
  }
};

export const deleteComment = async (project, commentId, user, ideaId) => {
  if (!project.organizationId)
    throw new Error("OrganizationId not found in deleteComment");
  if (!project.projectId)
    throw new Error("ProjectId not found in deleteComment");
  if (!commentId) throw new Error("CommentId not found in deleteComment");
  try {
    const commentRef = doc(db, `comments/${commentId}`);
    const commentDocSnapshot = await getDoc(commentRef);
    if (!commentDocSnapshot.exists()) {
      throw new Error("Comment not found in deleteComment");
    }
    const ideaDocRef = doc(
      db,
      `organizations/${project.organizationId}/projects/${project.projectId}/ideas/${ideaId}`,
    );
    const ideaDocSnapshot = await getDoc(ideaDocRef);
    if (!ideaDocSnapshot.exists()) {
      throw new Error("Idea not found in deleteComment");
    }
    if (
      user.userId === commentDocSnapshot.data().userId ||
      user.isAdmin ||
      user.isModerator
    ) {
      await deleteDoc(commentRef);
      await updateDoc(ideaDocRef, {
        commentCount: increment(-1),
      });
      return commentId;
    }
    throw new Error("User not authorized to delete comment");
  } catch (error) {
    throw new Error(error, "Error in deleteComment() action");
  }
};

// Delete idea
export const deleteIdea = async (project, ideaId, user) => {
  if (!project?.organizationId)
    throw new Error("No organizationId provided in deleteIdea");
  if (!project?.projectId)
    throw new Error("No projectId provided in deleteIdea");
  if (!ideaId) throw new Error("No ideaId provided in deleteIdea");
  try {
    const docRef = doc(
      db,
      `organizations/${project.organizationId}/projects/${project.projectId}/ideas/${ideaId}`,
    );
    const idea = await getDoc(docRef);
    if (!idea.exists()) throw new Error("Idea has not been found");

    if (
      idea.data().userId === user.userId ||
      user.isAdmin ||
      user.isModerator
    ) {
      await deleteDoc(docRef);
    } else {
      throw new Error(`cant delete this users${idea.data().userHandle} idea`);
    }
  } catch (error) {
    throw new Error(error, "error in deleteIdeaFunc");
  }
};
